/**
 * @file LookSizeGroup.tsx
 * @description Группа «Ширина и высота» секции (phase19.16 Р2;
 * uiux-design-r2 §2). SB120 этап 1 «секция-герой» (uiux-sb120-stage1
 * «Дополнение» §2.1, §2.8; данные db-design-sb120-stage1 §1):
 *
 * ```
 * Ширина блока        [Во всю]          ↺ по сетке 1280
 * ├──────────────●────┤ [ 1280 ] px  [По сетке 1280] [Узкая 768]
 *  Содержимое ≈ 1140 px: по 70 px по бокам (см. «Отступы»)
 * [ ПК | Телефон ]
 * Высота — 4 схемы · Содержимое в блоке — 3×3 + «Растянуть»
 * [✓] Фон за рамку сайта — до краёв окна (SB120 этап 2, uiux-sb120-stage2 §3)
 * [✓] На подложке карточки сайта · Скругление · Тень
 * ```
 *
 * Ширина — `look.container`: число 320–1920 или «Во всю» (`"full"`, без
 * предела и боков); нет ключа — по сетке 1280. Вкладки ПК/Телефон — общие
 * для высоты и положения содержимого.
 *
 * SB120 этап 2: тень и скругление «как у сайта» — числа темы
 * (`shapes.cards.shadow`, `shapes.radius.card`); под шириной —
 * `OutsideFrameField` («Фон за рамку сайта», `look.outsideFrame`).
 *
 * Подложка (замечание владельца 2026-10-19 «тень и скругление не видны»):
 * под полями — `CardSurfaceNotes` (подложка сливается с фоном страницы,
 * «Во всю» прячет края); пока курсор или фокус на флажке, скруглении или
 * тени — `space-highlight { part: "card" }`: канва обводит подложку
 * (`useCardHighlight`: снять при размонтировании, повторить после включения
 * подложки и смены ширины).
 */

import { DEFAULT_SITE_THEME, SECTION_LOOK_LIMITS, type SectionLook } from "@chaiberi/shared";
import { useState, type FocusEvent } from "react";

import { CheckRow } from "../../../site-view/CheckRow";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { DeviceTabs } from "../fields/DeviceTabs";
import { LookWarningNote } from "../fields/LookWarningNote";
import { useSectionForm } from "../section-form-context";
import { CardSurfaceNotes } from "./CardSurfaceNotes";
import { ContentPlacementField } from "./ContentPlacementField";
import { HeightModeField } from "./HeightModeField";
import { OutsideFrameField } from "./OutsideFrameField";
import { WidthField } from "./WidthField";
import { useCardHighlight } from "../../../../hooks/site-builder/useCardHighlight";
import { mediaAspect } from "../../../../hooks/site-builder/useMediaSizes";
import type { Device } from "../../../../lib/site-builder/look-size";
import { formatPx } from "../../../../lib/site-builder/look-space";

const LIM = SECTION_LOOK_LIMITS;
/** Ступени тени 0 / 50 / 100; тень карточек сайта — своей ступенью «Как у сайта N», если её нет среди них. */
function shadowPresets(site: number) {
  const steps = [0, 50, 100];
  const list = steps.map((value) => ({ label: value === site ? `Как у сайта ${value}` : String(value), value }));
  if (!steps.includes(site)) list.push({ label: `Как у сайта ${formatPx(site)}`, value: site });
  return list.sort((a, b) => a.value - b.value);
}

export function LookSizeGroup({ look, onChange }: { look: SectionLook; onChange: (look: SectionLook) => void }) {
  const { preview, mediaSizes, theme, highlight } = useSectionForm();
  const [device, setDevice] = useState<Device>("desktop");
  const shapes = (theme ?? DEFAULT_SITE_THEME).shapes;
  // Тень подложки «как у сайта» — тень карточек темы, % (SB120 этап 2: число, 50 — прежняя «С тенью»).
  const siteShadow = shapes.cards.shadow;
  const bg = look.bg.kind === "media" ? look.bg : null;
  const fileAspect = mediaAspect(mediaSizes, device === "phone" ? (bg?.mediaIdPhone ?? bg?.mediaId) : bg?.mediaId);
  const setCard = (patch: Partial<SectionLook["card"]>) => {
    const card = { ...look.card, ...patch };
    // undefined — «как у сайта»: ключ снимается.
    for (const key of ["radius", "shadow"] as const) if (card[key] === undefined) delete card[key];
    onChange({ ...look, card });
  };
  // Контур подложки на канве, пока курсор над её настройками или фокус внутри; переход фокуса между ними не мигает.
  const outline = useCardHighlight(highlight, `${look.card.on}:${look.container ?? "grid"}`);
  const leaveFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) outline("focus", false);
  };

  return (
    <div className="flex flex-col gap-4">
      <WidthField look={look} onChange={onChange} />
      <OutsideFrameField look={look} onChange={onChange} />
      <LookWarningNote paths={["look.container"]} />
      <DeviceTabs value={device} onChange={setDevice} label="Высота и положение: экран" phoneOwn={look.heightPhone !== undefined || look.placePhone !== undefined} />
      <HeightModeField look={look} device={device} onChange={onChange} fileAspect={fileAspect} />
      <ContentPlacementField look={look} device={device} onChange={onChange} />
      <div
        className="flex flex-col gap-4"
        onMouseEnter={() => outline("hover", true)}
        onMouseLeave={() => outline("hover", false)}
        onFocus={() => outline("focus", true)}
        onBlur={leaveFocus}
      >
        <CheckRow checked={look.card.on} onChange={(on) => setCard({ on })}>
          На подложке карточки сайта
        </CheckRow>
        {look.card.on ? (
          <div className="flex flex-col gap-3 border-l-2 border-outline-variant pl-3">
            <NumberSliderField
              label="Скругление"
              value={look.card.radius}
              inherit={{ value: shapes.radius.card, label: "как у сайта" }}
              {...LIM.cardRadius}
              step={1}
              unit="px"
              display={{ factor: 1, decimals: 2 }}
              onChange={(radius) => setCard({ radius })}
              onPreview={(v) => preview("look.card.radius", v)}
            />
            <NumberSliderField
              label="Тень"
              value={look.card.shadow}
              inherit={{ value: siteShadow, label: "как у сайта" }}
              {...LIM.cardShadow}
              step={1}
              unit="%"
              presets={shadowPresets(siteShadow)}
              hint={`0 — без тени, 50 — прежняя тень карточек, 100 — заметно плотнее.${siteShadow > 0 ? "" : " У карточек сайта тени нет — «как у сайта» = 0."}`}
              onChange={(shadow) => setCard({ shadow })}
              onPreview={(v) => preview("look.card.shadow", v)}
            />
            <CardSurfaceNotes look={look} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
